import { ArrowRightIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import React, { useMemo, useState } from "react";

import Card from "components/atoms/Card";
import Spinner from "components/atoms/Spinner";

export interface UsageDetailItem {
  keyName: string;
  keyValue: string;
  usage: number;
  count: number;
}

export interface RouteAverageRow {
  category: string;
  totalCalls: number;
  avgBilledTokens: number;
  totalBilledTokens: number;
}

interface PlatformUsageBreakdownProps {
  /** Grouped usage for the current billing period, keyed by group value. */
  usageDetail: Record<string, UsageDetailItem> | null;
  groupModes: string[][];
  groupMode: string[];
  onGroupModeChange: (mode: string[]) => void;
  detailLoading: boolean;
  /** Customer route averages over the trailing window, for the Cost view. */
  routeAverages: RouteAverageRow[];
  routeAveragesLoading: boolean;
  routeWindowDays: number;
  refillPricePerM: number;
  refillRateLabel?: string;
  periodLabel?: string;
  insightsUnlocked: boolean;
  onOpenInsights: () => void;
  onOpenUpgrade: () => void;
}

const COLLAPSED_ROW_COUNT = 8;

const formatCompact = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return "0";
  if (value >= 1e9) return `${(value / 1e9).toFixed(value >= 1e10 ? 0 : 1)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(value >= 1e7 ? 0 : 1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(value >= 1e4 ? 0 : 1)}K`;
  return Math.round(value).toLocaleString();
};

const formatUsd = (amount: number): string =>
  amount.toLocaleString("en-US", { style: "currency", currency: "USD" });

const TYPE_LABELS: Record<string, string> = {
  api: "API Calls",
  textai: "Text AI",
  visionai: "Vision AI",
  chatai: "Advisor / Chat",
  aisearch: "AI Search",
  semanticsearch: "AI Search",
};

/** Keeps long request paths readable without a tooltip-only truncation. */
const shortenPath = (path: string, max = 52): string => {
  if (path.length <= max) return path;
  const segments = path.split("/");
  if (segments.length > 3) {
    const tail = segments.slice(-2).join("/");
    return `${segments[0]}/${segments[1]}/…/${tail}`.slice(0, max + 8);
  }
  return `${path.slice(0, max)}…`;
};

/**
 * The distinguishing part of a group label. Request paths share a long common
 * prefix, so the tail is what actually identifies them in a narrow stat tile.
 */
const groupShortLabel = (label: string): string => {
  if (!label.includes("/")) return label;
  const segments = label.split("/").filter(Boolean);
  return segments.slice(-2).join("/") || label;
};

const ShareBar = ({ pct }: { pct: number }) => (
  <div className="flex items-center gap-2">
    <div className="h-1.5 w-16 flex-shrink-0 overflow-hidden rounded-full bg-gray-100">
      <div
        className="h-full rounded-full bg-indigo-500"
        style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
      />
    </div>
    <span className="w-10 text-right tabular-nums text-sm text-gray-500">
      {pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}%
    </span>
  </div>
);

const Stat = ({
  label,
  value,
  hint,
  title,
}: {
  label: string;
  value: string;
  hint?: string;
  /** Full value, when `value` is abbreviated to fit. */
  title?: string;
}) => (
  <div className="min-w-0 rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2">
    <p className="text-sm font-medium uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <p className="truncate text-lg font-semibold text-gray-900" title={title}>
      {value}
    </p>
    {hint && <p className="truncate text-sm text-gray-500">{hint}</p>}
  </div>
);

const PlatformUsageBreakdown: React.FC<PlatformUsageBreakdownProps> = ({
  usageDetail,
  groupModes,
  groupMode,
  onGroupModeChange,
  detailLoading,
  routeAverages,
  routeAveragesLoading,
  routeWindowDays,
  refillPricePerM,
  refillRateLabel,
  periodLabel,
  insightsUnlocked,
  onOpenInsights,
  onOpenUpgrade,
}) => {
  const [view, setView] = useState<"detail" | "cost">("detail");
  const [expanded, setExpanded] = useState(false);

  const isPathGrouping = groupMode[1] === "path";
  const isTypeGrouping = groupMode[1] === "type";

  const rows = useMemo(() => {
    const items = Object.values(usageDetail || {});
    return items
      .map((item) => {
        const raw = item.keyValue || "—";
        const label = isTypeGrouping
          ? TYPE_LABELS[raw.trim().toLowerCase()] || raw
          : raw;
        return {
          raw,
          label,
          usage: Math.max(0, item.usage),
          count: Math.max(0, item.count),
        };
      })
      .sort((a, b) => b.usage - a.usage);
  }, [usageDetail, isTypeGrouping]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          usage: acc.usage + row.usage,
          count: acc.count + row.count,
        }),
        { usage: 0, count: 0 }
      ),
    [rows]
  );

  const costTotals = useMemo(
    () =>
      routeAverages.reduce(
        (acc, row) => ({
          calls: acc.calls + row.totalCalls,
          tokens: acc.tokens + row.totalBilledTokens,
        }),
        { calls: 0, tokens: 0 }
      ),
    [routeAverages]
  );

  const activeRowCount = view === "cost" ? routeAverages.length : rows.length;
  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_ROW_COUNT);
  const isLoading = view === "cost" ? routeAveragesLoading : detailLoading;

  const tabs: Array<{ key: string; label: string; onSelect: () => void }> = [
    ...groupModes.map((mode) => ({
      key: `group-${mode[1]}`,
      label: `By ${mode[0].replace(/^Request /, "")}`,
      onSelect: () => {
        setView("detail");
        setExpanded(false);
        onGroupModeChange(mode);
      },
    })),
    {
      key: "cost",
      label: "Cost by route",
      onSelect: () => {
        setView("cost");
        setExpanded(false);
      },
    },
  ];

  const activeTabKey = view === "cost" ? "cost" : `group-${groupMode[1]}`;

  return (
    <Card className="sm:p-6" borderRequired>
      {/* --- Header --- */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Usage Breakdown
          </h3>
          <p className="text-sm text-gray-500">
            {view === "cost"
              ? `Your last ${routeWindowDays} days of calls, priced at your tier's refill rate.`
              : periodLabel
              ? `Current billing period · ${periodLabel}`
              : "Current billing period"}
          </p>
        </div>
        {insightsUnlocked ? (
          <button
            type="button"
            onClick={onOpenInsights}
            className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 transition-colors hover:bg-indigo-50"
          >
            Full route analytics
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onOpenUpgrade}
            title="Per-route analytics, reliability and trends are available on Growth and Pro."
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100"
          >
            <LockClosedIcon className="h-4 w-4" />
            Unlock route analytics
          </button>
        )}
      </div>

      {/* --- Summary stats --- */}
      <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
        {view === "cost" ? (
          <>
            <Stat
              label={`Calls (${routeWindowDays}d)`}
              value={costTotals.calls.toLocaleString()}
            />
            <Stat
              label={`Tokens (${routeWindowDays}d)`}
              value={formatCompact(costTotals.tokens)}
            />
            <Stat
              label="Avg tokens / call"
              value={formatCompact(
                costTotals.calls > 0 ? costTotals.tokens / costTotals.calls : 0
              )}
            />
            <Stat
              label="Reference value"
              value={formatUsd((costTotals.tokens * refillPricePerM) / 1e6)}
              hint={refillRateLabel ? `at ${refillRateLabel}` : undefined}
            />
          </>
        ) : (
          <>
            <Stat label="Tokens used" value={formatCompact(totals.usage)} />
            <Stat label="Calls" value={totals.count.toLocaleString()} />
            <Stat
              label="Avg tokens / call"
              value={formatCompact(
                totals.count > 0 ? totals.usage / totals.count : 0
              )}
            />
            <Stat
              label="Top consumer"
              value={rows[0] ? groupShortLabel(rows[0].label) : "—"}
              title={rows[0]?.label}
              hint={
                rows[0] && totals.usage > 0
                  ? `${((rows[0].usage / totals.usage) * 100).toFixed(
                      0
                    )}% of tokens`
                  : undefined
              }
            />
          </>
        )}
      </div>

      {/* --- View tabs --- */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={tab.onSelect}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
              activeTabKey === tab.key
                ? "bg-slate-700 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* --- Table --- */}
      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200">
        {isLoading ? (
          <div className="py-8">
            <Spinner className="!h-8 !w-8 !mr-0" />
          </div>
        ) : activeRowCount === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-gray-500">
            {view === "cost"
              ? `No route usage recorded in the last ${routeWindowDays} days.`
              : "No usage recorded for this billing period yet."}
          </p>
        ) : view === "cost" ? (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr className="text-left text-sm uppercase tracking-wider text-gray-600">
                <th className="py-2 px-3 font-semibold">Route</th>
                <th className="whitespace-nowrap py-2 px-3 text-right font-semibold">
                  Avg / call
                </th>
                <th className="whitespace-nowrap py-2 px-3 text-right font-semibold">
                  Calls
                </th>
                <th className="whitespace-nowrap py-2 px-3 text-right font-semibold">
                  Tokens
                </th>
                <th className="whitespace-nowrap py-2 px-3 text-right font-semibold">
                  Per 1K calls
                </th>
                <th className="whitespace-nowrap py-2 px-3 text-right font-semibold">
                  Value
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {routeAverages.map((row) => (
                <tr key={row.category} className="hover:bg-gray-50">
                  <td className="py-2 px-3 font-medium text-gray-900">
                    {row.category}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-indigo-700 tabular-nums">
                    {formatCompact(row.avgBilledTokens)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums text-gray-700">
                    {row.totalCalls.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums text-gray-700">
                    {formatCompact(row.totalBilledTokens)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums text-gray-900">
                    {formatUsd(
                      (row.avgBilledTokens * 1000 * refillPricePerM) / 1e6
                    )}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold tabular-nums text-gray-900">
                    {formatUsd((row.totalBilledTokens * refillPricePerM) / 1e6)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-gray-900">
                <td className="py-2 px-3">Total</td>
                <td className="py-2 px-3 text-right tabular-nums">
                  {formatCompact(
                    costTotals.calls > 0
                      ? costTotals.tokens / costTotals.calls
                      : 0
                  )}
                </td>
                <td className="py-2 px-3 text-right tabular-nums">
                  {costTotals.calls.toLocaleString()}
                </td>
                <td className="py-2 px-3 text-right tabular-nums">
                  {formatCompact(costTotals.tokens)}
                </td>
                <td className="py-2 px-3 text-right text-gray-400">—</td>
                <td className="py-2 px-3 text-right tabular-nums">
                  {formatUsd((costTotals.tokens * refillPricePerM) / 1e6)}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr className="text-left text-sm uppercase tracking-wider text-gray-600">
                <th className="py-2 px-3 font-semibold">{groupMode[0]}</th>
                <th className="whitespace-nowrap py-2 px-3 text-right font-semibold">
                  Calls
                </th>
                <th className="whitespace-nowrap py-2 px-3 text-right font-semibold">
                  Tokens
                </th>
                <th className="whitespace-nowrap py-2 px-3 text-right font-semibold">
                  Avg / call
                </th>
                <th className="py-2 px-3 font-semibold">Share of tokens</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleRows.map((row) => (
                <tr key={row.raw} className="hover:bg-gray-50">
                  <td
                    className="max-w-[22rem] py-2 px-3 font-medium text-gray-900"
                    title={isPathGrouping ? row.raw : undefined}
                  >
                    {isPathGrouping ? (
                      <span className="font-mono text-sm">
                        {shortenPath(row.raw)}
                      </span>
                    ) : (
                      row.label
                    )}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums text-gray-700">
                    {row.count.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold tabular-nums text-gray-900">
                    {formatCompact(row.usage)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums text-indigo-700">
                    {formatCompact(row.count > 0 ? row.usage / row.count : 0)}
                  </td>
                  <td className="py-2 px-3">
                    <ShareBar
                      pct={
                        totals.usage > 0 ? (row.usage / totals.usage) * 100 : 0
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {view === "detail" && rows.length > COLLAPSED_ROW_COUNT && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 self-start text-sm font-semibold text-indigo-700 hover:text-indigo-500"
        >
          {expanded
            ? "Show fewer"
            : `Show all ${rows.length} ${groupMode[0].toLowerCase()}s`}
        </button>
      )}

      {view === "cost" && routeAverages.length > 0 && (
        <p className="mt-2 text-sm text-gray-500">
          Reference only — overage is billed at your refill rate only after your
          included monthly tokens run out. Calls billed at 2.5M tokens or more
          are excluded as outliers.
        </p>
      )}
    </Card>
  );
};

export default PlatformUsageBreakdown;
